import * as Api from '@ViewModels';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { IPhoneCall } from '../../extViewmodels';
import { AIConversation } from '../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'sales-live-coaching-conversations';
const BY_ID_QUERY_KEY = 'sales-live-coaching-conversation-by-id';

/**
 * The server caps `pageSize` at 25 (and defaults to 25). We request that maximum so each page —
 * including each "Load more" fetch — pulls a full 25 conversations.
 */
const PAGE_SIZE = 25;

type QueryParams = {
	enabled?: boolean;
	refetchInterval?: number | false;
	impersonationContext?: Api.IImpersonationContext;
	/** Scope to a single rep (creator id). Falsy = all reps. Applied server-side via a CreatorId criterion. */
	creatorId?: string;
	/** Inclusive lower bound as a `YYYY-MM-DD` local date; sent as a `CreationDate` Gt (→ server `$gte`) criterion. */
	startDate?: string;
	/** Inclusive upper bound as a `YYYY-MM-DD` local date; sent as a `CreationDate` Lt (→ server `$lte`) criterion. */
	endDate?: string;
	/**
	 * Case-insensitive title substring, sent (trimmed) as a `Title` criterion. Matches persisted titles
	 * only, and the server REQUIRES an accompanying ≤30-day `CreationDate` range — so it is sent only
	 * when {@link isTitleSearchRangeValid} holds for `startDate`/`endDate` (else the API 400s).
	 */
	titleSearch?: string;
};

/** Max `CreationDate` window (days) the server allows a `Title` search to span. */
export const TITLE_SEARCH_MAX_RANGE_DAYS = 30;
const TITLE_SEARCH_MAX_RANGE_MS = TITLE_SEARCH_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;

/**
 * Whether a `Title` search may be sent for the given date bounds: both must be set and the window
 * actually sent (start-of-day → end-of-day) must be ≤ {@link TITLE_SEARCH_MAX_RANGE_DAYS}. The server
 * rejects a `Title` criterion without such a range, so the query and the UI both gate on this.
 */
export function isTitleSearchRangeValid(startDate?: string, endDate?: string): boolean {
	if (!startDate || !endDate) {
		return false;
	}
	const startMs = new Date(`${startDate}T00:00:00`).getTime();
	const endMs = new Date(`${endDate}T23:59:59.999`).getTime();
	return (
		!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs >= startMs && endMs - startMs <= TITLE_SEARCH_MAX_RANGE_MS
	);
}

/**
 * Builds the coaching filter's criteria from the active filters. Every criterion carries its data in
 * a single `value` string (+ `op` for date bounds) — the server drops the `values` dict, so it is
 * never used. `property` is the numeric enum ordinal (the enum has no string converter). Date bounds
 * are converted from a `YYYY-MM-DD` local date to an ISO-8601 instant: the lower bound at the start of
 * the day, the upper at the very end, so an inclusive `$gte`/`$lte` covers the whole selected day
 * regardless of the viewer's timezone. The `Title` criterion is sent only alongside a valid ≤30-day
 * date range ({@link isTitleSearchRangeValid}) — the server 400s otherwise.
 */
function buildCoachingFilter(params: QueryParams): Api.AIConversationFilter {
	const criteria: Api.AIConversationFilter['criteria'] = [
		{
			property: Api.AIConversationFilterProperty.ConversationType,
			value: Api.AIConversationType.SalesLiveCoaching,
		},
	];

	if (params.creatorId) {
		criteria.push({ property: Api.AIConversationFilterProperty.CreatorId, value: params.creatorId });
	}
	if (params.startDate) {
		criteria.push({
			property: Api.AIConversationFilterProperty.CreationDate,
			op: Api.FilterOperator.Gt,
			value: new Date(`${params.startDate}T00:00:00`).toISOString(),
		});
	}
	if (params.endDate) {
		criteria.push({
			property: Api.AIConversationFilterProperty.CreationDate,
			op: Api.FilterOperator.Lt,
			value: new Date(`${params.endDate}T23:59:59.999`).toISOString(),
		});
	}
	const trimmedSearch = params.titleSearch?.trim();
	if (trimmedSearch && isTitleSearchRangeValid(params.startDate, params.endDate)) {
		criteria.push({ property: Api.AIConversationFilterProperty.Title, value: trimmedSearch });
	}

	return { criteria, op: Api.FilterOperator.And };
}

/**
 * One row from `aida/coffee/coaching/filter`: a coaching conversation already paired server-side
 * with the phone call it came from (and that call's resolved company + contacts). `phoneCall` is
 * null when a conversation has no linked call. This is the same `PhoneCallView` shape
 * `phonecall/filter` returns, nested under each conversation — so the screen no longer needs a
 * second request to attach call data.
 */
export interface AIConversationCallView {
	conversation: AIConversation;
	phoneCall: IPhoneCall | null;
	company: { id?: string; companyName?: string } | null;
	contacts: Api.IContact[] | null;
}

/**
 * Flattened view of the loaded pages, exposed as the query's `data` via react-query `select` so
 * consumers never reach into `data.pages` themselves. Pagination controls (`hasNextPage`,
 * `fetchNextPage`, `isFetchingNextPage`) stay on the top-level query result.
 */
export interface SalesLiveCoachingConversationsData {
	/** Conversations from every page loaded so far, in the server's order (newest-first). */
	conversations: AIConversation[];
	/** Lookup from conversation id to its full call view (conversation + phoneCall + company/contacts). */
	callViewsByConversationId: Map<string, AIConversationCallView>;
	/** Total conversations matching the filter across ALL pages (from the most recent page response). */
	totalCount: number | null;
}

/**
 * Fetches `SalesLiveCoaching` AI conversations across the whole account, one page (max 25) at a time
 * via the cursor-paginated, admin-only `POST aida/coffee/coaching/filter` endpoint. That route is
 * Coffee-specific and inherently account-wide (it applies an account-wide context server-side — there
 * is no `accountWide` request param) and is account-admin only, returning 403 to non-admins;
 * `ConversationList` surfaces that 403 gracefully. Each row pairs the conversation with its joined
 * phone call (see {@link AIConversationCallView}), so the screen needs no second request.
 *
 * Unlike `useTysonCoachingConversations`, which scopes results to the signed-in user via a
 * `CreatorId` criterion, this lists conversations created by ALL reps in the account.
 *
 * Returns a `useInfiniteQuery` result whose `data` is shaped by `select` into a flat
 * `{ conversations, totalCount }` ({@link SalesLiveCoachingConversationsData}); call
 * `fetchNextPage()` (gated on `hasNextPage`) to append the next page.
 */
export function useSalesLiveCoachingConversationsQuery(options?: QueryParams) {
	const userSession = useUserSession();
	const accountId = userSession.account?.id;

	const impersonationContext = options?.impersonationContext;
	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	const filter = buildCoachingFilter(options ?? {});
	// Active filters are part of the key so changing a filter re-queries from the first page (the
	// server applies them, so cached pages for a different filter must not be reused).
	const filterKey = {
		creatorId: options?.creatorId,
		startDate: options?.startDate,
		endDate: options?.endDate,
		// Only key on the title when it will actually be sent (valid ≤30-day range), so typing a title
		// without a valid range doesn't churn the cache with no payload change.
		titleSearch: isTitleSearchRangeValid(options?.startDate, options?.endDate)
			? options?.titleSearch?.trim() || undefined
			: undefined,
	};

	return useInfiniteQuery({
		queryKey: [BASE_QUERY_KEY, accountId, impersonationKey, filterKey],
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<AIConversationCallView>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'aida/coffee/coaching/filter',
					// `pageToken` is '' for the first page; `composeApiUrl` strips empty values, so no
					// `pageToken` query param is sent until we have a cursor from a prior page. Sort
					// defaults server-side to creationDate desc (the route's `[Sort]` attribute), mirroring
					// the `aiagenttemplate/filter` wiring — so we send only pageSize + pageToken.
					queryParams: {
						pageSize: PAGE_SIZE,
						pageToken: pageParam,
					},
					impersonationContext,
				}),
				'POST',
				filter
			),
		initialPageParam: '',
		// `pageToken` is the cursor for the NEXT page; null/absent means there are no more pages.
		// Coerce null -> undefined so react-query treats it as "no next page" and clears `hasNextPage`.
		getNextPageParam: lastPage => lastPage.pageToken ?? undefined,
		select: (data): SalesLiveCoachingConversationsData => {
			const views = data.pages.flatMap(page => page.values ?? []);
			return {
				conversations: views.map(view => view.conversation),
				callViewsByConversationId: new Map(views.map(view => [view.conversation.id, view])),
				totalCount: data.pages.at(-1)?.totalCount ?? null,
			};
		},
		enabled: options?.enabled ?? true,
		refetchInterval: options?.refetchInterval,
		meta: {
			toastErrorMessage: 'Failed to load coaching conversations',
			errorLogging: { fn: 'useSalesLiveCoachingConversationsQuery' },
		},
	});
}

type ByIdQueryParams = {
	id: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
};

/**
 * Fetches a single coaching conversation's full call view by id via `GET aida/coffee/coaching/{id}`
 * — the same admin-only, account-wide route family as the list `filter`, so it returns the identical
 * {@link AIConversationCallView} shape (conversation + joined phoneCall + company/contacts). This
 * backs deep links (`?convo=<id>`) to conversations that aren't in the loaded infinite-scroll pages:
 * see {@link useCoachingConversationCallView}, which prefers the already-loaded list data and falls
 * back to this per-id fetch only when the target isn't loaded. Disabled when `id` is empty.
 */
export function useSalesLiveCoachingConversationByIdQuery(options: ByIdQueryParams) {
	const userSession = useUserSession();
	const { id, impersonationContext } = options;

	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	return useQuery({
		queryKey: [BY_ID_QUERY_KEY, id, impersonationKey],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<AIConversationCallView>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `aida/coffee/coaching/${id}`,
					impersonationContext,
				}),
				'GET'
			),
		enabled: (options.enabled ?? true) && Boolean(id),
		meta: {
			// 404 (id not found / not a coaching conversation) and 403 (the list already renders a
			// dedicated forbidden state) are handled inline in the UI, so suppress their toasts.
			toastErrorMessage: (error: Error) => {
				const code = (error as { systemCode?: number }).systemCode;
				return code === 404 || code === 403 ? '' : 'Failed to load coaching conversation';
			},
			errorLogging: { fn: 'useSalesLiveCoachingConversationByIdQuery' },
		},
	});
}
